/**
 * SetupWizard
 * CCH Care — Phase 2.5
 * Last Modified: 2026-07-07
 *
 * In-app interactive setup guide. Shows on dashboard when facility setup is incomplete.
 * Each step has an inline form with:
 * - List of already-created items
 * - Form that clears after save with success confirmation
 * - "Save & Add Another" + "Done — Next Step" buttons
 *
 * Fix: patient_assignments added to REPEATABLE_STEPS so saving one assignment
 * does not auto-navigate to the dashboard. A manual "Done — Go to Dashboard"
 * button is shown at the bottom of Step 9 instead.
 *
 * ESLint / encoding fixes:
 * - Removed unused serializeAllergies, parseAllergies imports
 * - Added decodeHtml applied to all API-returned names (staff, residents, alerts)
 * - Fixed fetchStatus useCallback dep — activeStep logic moved to separate useEffect
 * - Fixed div onClick elements: added role/tabIndex/onKeyDown
 * - Fixed unescaped entities
 * - Removed unused alerts destructuring warning in renderStepForm
 *
 * Dark mode: replaced all hardcoded hex in Field, Select, and inline
 *            styles throughout with COLORS.* tokens.
 *
 * Changes (2026-04-19) — Bulk room creation:
 * - Step 2 (rooms) now has a "Single Room / Bulk Create" toggle. Bulk mode
 *   lets admin enter a numeric room-number range (e.g., 101–120) + beds per
 *   room and creates all rooms in one operation. Single-room mode is the
 *   existing flow, fully preserved. Mode is stored in formData.bulk_mode
 *   and preserved across saves (so admin can bulk-create in Unit A, then
 *   bulk-create in Unit B without re-toggling).
 * - Bulk save shows a specific success message like "Created 19 rooms
 *   (1 skipped because they already exist) with 38 bed(s) total".
 *
 * Changes (2026-04-20) — Alpha-concat cleanup + label a11y:
 * - Editing-assignment banner border: `${COLORS.primary}44` → `${COLORS.primaryAlpha}`.
 *   The template-literal concat was producing invalid CSS ("var(--color-primary)44").
 * - Just-saved success banner border: `rgba(46,204,113,0.3)` (hardcoded accent
 *   RGB) → `${COLORS.accentAlpha}` so the border themes with the rest of the
 *   banner.
 * - Care-shift status pills: added parallel `statusBgColors` dict mapping
 *   status → COLORS.*Alpha token. Previous `statusColors[s.status] + '22'`
 *   was the same latent concat-with-CSS-var bug fixed in StaffManagement.
 * - Field and Select subcomponents: added useId() + htmlFor/id pairing so
 *   labels associate with their form controls (jsx-a11y/label-has-associated-control).
 *
 * Changes (2026-04-22) — Bed label presentation consistency:
 * - Residents-form Bed dropdown now renders `'Bed ' + b.bed_label` so the
 *   bed identifier is prefixed the same way the Step 3 list view prefixes
 *   it ('Bed ' + b.bed_label in CreatedItemsList). Previously the dropdown
 *   rendered bare `b.bed_label`, which looked inconsistent with the list
 *   (e.g. list showed "Bed A" while dropdown showed "A"). Paired with a
 *   backend fix (rooms.php) that stops storing auto-gen labels as "Bed N"
 *   and a one-off migration that strips the prefix from existing rows —
 *   see migration_bed_label_strip_prefix.sql. Net effect: `bed_label` is
 *   now canonically the identifier only, and the "Bed " prefix is added
 *   at every render site.
 *
 * Changes (2026-04-23) — Standing Alerts optional framing:
 * - Removed the "Skip for now — I'll add alerts later" small-text link at
 *   the bottom of Step 5. Paired with a backend change in setup-status.php
 *   that marks the standing_alerts step complete whenever residents > 0.
 *   Once residents are enrolled, the step auto-completes and Step 6
 *   unblocks naturally via the existing isBlocked gate — no escape-hatch
 *   link needed. Keeping the link would reinforce the "this is something
 *   to escape from" framing we're trying to eliminate. The step label and
 *   description are updated server-side in setup-status.php, so no copy
 *   changes needed here; the accordion header reads directly from
 *   `status.steps[i].label` and `status.steps[i].description`.
 *
 * Changes (2026-06-29) — Room editing (Step 2):
 * - Rooms are now editable inline from the "Already Created" list, matching
 *   the units/residents/alerts edit affordance. Tapping a room row loads its
 *   number + bed count into the form; the button becomes "Update Room".
 * - Five wiring points: (1) 'rooms' added to CreatedItemsList `editable`;
 *   (2) rooms list items now carry `raw: r` so handleEdit receives the full
 *   room (room_id/unit_id/room_number/bed_count); (3) new 'rooms' case in
 *   handleEdit seeds the form and sets editingId; (4) handleSave routes to
 *   adminApi.updateRoom when editingId is set (create path unchanged);
 *   (5) the Single/Bulk ModeToggle is hidden while editing (you edit one
 *   existing room, not a range) — gated on formData.room_id.
 * - Backend guards (api/admin/rooms.php PUT/DELETE) enforce: block rename
 *   when the room has active placements (historical-label preservation),
 *   UNIQUE-collision friendly 409, bed-count floor >= occupied, and refuse
 *   to deactivate an occupied room. Pre-GA these never trigger (zero
 *   placements) but protect history once real residents load.
 *
 * Changes (2026-07-07) — Room + Unit deletion (Steps 1 & 2):
 * - Rooms and Units now have an inline delete ("✕") affordance in their
 *   "Already Created" rows, alongside the existing tap-to-edit. Previously
 *   the DELETE endpoints + client wrappers (adminApi.deleteRoom/deleteUnit)
 *   existed but nothing in the wizard called them.
 * - Wiring: (1) 'rooms' + 'units' added to CreatedItemsList `deletable`;
 *   (2) units list items carry `raw: u` (rooms already did) so the row has
 *   the id; (3) a new generic handleDeleteItem() routes to the right wrapper
 *   by stepKey, distinct from the assignment-specific handleDeleteAssignment;
 *   (4) onDelete is now passed for rooms/units in the CreatedItemsList call.
 * - Delete is a soft-deactivate (is_active = 0). Backend guards return a 409
 *   when blocked: a room/unit with active placements can't be deactivated,
 *   and deactivating a unit cascades to its rooms + beds when empty. Those
 *   409 messages surface in the existing red "Save failed" banner.
 * - Confirm copy differs by type: the unit confirm warns that its rooms and
 *   beds will also be deactivated (the backend cascade).
 */
import { useState, useEffect, useCallback, useId } from 'react';
import * as adminApi from '../../../api/admin';
import { COLORS } from '../../../config/constants';
import AllergyPicker from './AllergyPicker';

function decodeHtml(str) {
  if (!str) return str;
  return str
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}

const STEP_ICONS = ['🏢', '🚪', '🛏️', '👤', '⚠️', '⏰', '📅', '👩‍⚕️', '📋'];
const REPEATABLE_STEPS = ['units', 'rooms', 'beds', 'residents', 'standing_alerts', 'shift_assignments', 'patient_assignments'];

// Bulk room safety caps — must match backend (api/admin/rooms.php)
const ROOMS_BULK_MAX_RANGE    = 200;
const ROOMS_BULK_MAX_BED_COUNT = 10;

function getStepNoun(key) {
  const nouns = {
    units: 'Unit', rooms: 'Room', beds: 'Bed', residents: 'Resident',
    standing_alerts: 'Alert', shift_assignments: 'Assignment', patient_assignments: 'Assignment',
  };
  return nouns[key] || '';
}

export default function SetupWizard({ onComplete }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [stepError, setStepError] = useState(null);
  const [justSaved, setJustSaved] = useState(null);

  const [units, setUnits] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [residents, setResidents] = useState([]);
  const [shifts, setShifts] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [patientData, setPatientData] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await adminApi.getSetupStatus();
      if (data.success) setStatus(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (status && activeStep === null) {
      const first = status.steps.findIndex(s => !s.complete);
      if (first >= 0) setActiveStep(first);
    }
  }, [status, activeStep]);

  const refreshStepData = useCallback((key) => {
    if (!key) return;
    const today = new Date().toLocaleDateString('en-CA');
    if (key === 'units')
      adminApi.getUnits().then(d => d.success && setUnits(d.units)).catch(() => {});
    if (key === 'rooms') {
      adminApi.getUnits().then(d => d.success && setUnits(d.units)).catch(() => {});
      adminApi.getRooms().then(d => d.success && setRooms(d.rooms)).catch(() => {});
    }
    if (key === 'beds') {
      adminApi.getRooms().then(d => d.success && setRooms(d.rooms)).catch(() => {});
      adminApi.getBeds().then(d => d.success && setBeds(d.beds)).catch(() => {});
    }
    if (key === 'residents') {
      adminApi.getUnits().then(d => d.success && setUnits(d.units)).catch(() => {});
      adminApi.getRooms().then(d => d.success && setRooms(d.rooms)).catch(() => {});
      adminApi.getBeds().then(d => d.success && setBeds(d.beds)).catch(() => {});
      adminApi.getResidents().then(d => d.success && setResidents(d.residents)).catch(() => {});
    }
    if (key === 'standing_alerts') {
      adminApi.getResidents().then(d => d.success && setResidents(d.residents)).catch(() => {});
      adminApi.getStandingAlerts().then(d => d.success && setAlerts(d.alerts)).catch(() => {});
    }
    if (key === 'care_shifts')
      adminApi.getCareShifts(today).then(d => d.success && setShifts(d)).catch(() => {});
    if (key === 'shift_assignments') {
      adminApi.getUnits().then(d => d.success && setUnits(d.units)).catch(() => {});
      adminApi.getCareShifts(today).then(d => d.success && setShifts(d)).catch(() => {});
      adminApi.getStaff().then(d => d.success && setStaff(d.staff)).catch(() => {});
      adminApi.getShiftAssignments({ date: today }).then(d => d.success && setAssignments(d.assignments)).catch(() => {});
    }
    if (key === 'patient_assignments') {
      adminApi.getShiftAssignments({ date: today }).then(d => d.success && setAssignments(d.assignments)).catch(() => {});
      adminApi.getResidents().then(d => d.success && setResidents(d.residents)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (activeStep !== null && status) refreshStepData(status.steps[activeStep]?.key);
  }, [activeStep, status, refreshStepData]);

  const getSavedLabel = (stepKey) => {
    switch (stepKey) {
      case 'units': return formData.name || 'Unit';
      case 'rooms': {
        if (formData.bulk_mode) {
          return `Rooms ${formData.start_number}–${formData.end_number}`;
        }
        return 'Room ' + (formData.room_number || '');
      }
      case 'beds': return 'Bed ' + (formData.bed_label || '');
      case 'residents': return (((formData.first_name || '') + ' ' + (formData.last_name || '')).trim()) || 'Resident';
      case 'standing_alerts': return formData.alert_label || 'Alert';
      case 'shift_assignments': {
        const s = staff.find(m => String(m.membership_id) === String(formData.membership_id));
        return s ? decodeHtml(s.first_name + ' ' + s.last_name) : 'Assignment';
      }
      case 'patient_assignments': return 'Assignment';
      default: return 'Item';
    }
  };

  const handleSave = async (stepKey, andContinue = false) => {
    setSaving(true); setStepError(null); setJustSaved(null);
    const savedLabel = getSavedLabel(stepKey);
    try {
      let result;
      switch (stepKey) {
        case 'units':
          result = editingId
            ? await adminApi.updateUnit({ unit_id: editingId, name: formData.name, floor: formData.floor })
            : await adminApi.createUnit({ name: formData.name, floor: formData.floor });
          break;
        case 'rooms':
          if (editingId) {
            // Edit an existing room. Single-room only — no bulk edit.
            // Backend PUT guards rename/bed-floor/collision; unit is not editable here.
            result = await adminApi.updateRoom({
              room_id:     editingId,
              room_number: formData.room_number,
              bed_count:   parseInt(formData.bed_count) || 1,
            });
          } else if (formData.bulk_mode) {
            result = await adminApi.createRoom({
              unit_id:      formData.unit_id,
              start_number: parseInt(formData.start_number),
              end_number:   parseInt(formData.end_number),
              bed_count:    parseInt(formData.bed_count) || 1,
            });
          } else {
            result = await adminApi.createRoom({
              unit_id:     formData.unit_id,
              room_number: formData.room_number,
              bed_count:   formData.bed_count || 1,
            });
          }
          break;
        case 'beds': result = await adminApi.createBed({ room_id: formData.room_id, bed_label: formData.bed_label }); break;
        case 'residents':
          result = editingId
            ? await adminApi.updateResident({
                patient_id: editingId, first_name: formData.first_name, last_name: formData.last_name,
                date_of_birth: formData.date_of_birth, gender: formData.gender,
                unit_id: formData.unit_id, room_id: formData.room_id, bed_id: formData.bed_id,
                allergies: formData.allergies, medical_notes: formData.medical_notes,
                emergency_contact_name: formData.emergency_contact_name,
                emergency_contact_phone: formData.emergency_contact_phone,
              })
            : await adminApi.enrollResident({
                first_name: formData.first_name, last_name: formData.last_name,
                date_of_birth: formData.date_of_birth, gender: formData.gender,
                unit_id: formData.unit_id, room_id: formData.room_id, bed_id: formData.bed_id,
                allergies: formData.allergies, medical_notes: formData.medical_notes,
                emergency_contact_name: formData.emergency_contact_name,
                emergency_contact_phone: formData.emergency_contact_phone,
              });
          break;
        case 'standing_alerts':
          result = editingId
            ? await adminApi.updateStandingAlert({
                standing_alert_id: editingId, patient_id: formData.patient_id,
                alert_type: formData.alert_type, alert_label: formData.alert_label,
                severity: formData.severity || 'warning', details: formData.details,
              })
            : await adminApi.createStandingAlert({
                patient_id: formData.patient_id, alert_type: formData.alert_type,
                alert_label: formData.alert_label, severity: formData.severity || 'warning', details: formData.details,
              });
          break;
        case 'care_shifts': result = await adminApi.createCareShifts({ shift_date: formData.shift_date || new Date().toISOString().split('T')[0] }); break;
        case 'shift_assignments':
          if (editingId) await adminApi.deleteShiftAssignment(editingId);
          result = await adminApi.createShiftAssignment({
            care_shift_id: formData.care_shift_id,
            membership_id: formData.membership_id,
            unit_id: formData.unit_id,
          });
          break;
        case 'patient_assignments':
          result = await adminApi.assignPatients({
            shift_assignment_id: formData.shift_assignment_id,
            patient_ids: formData.patient_ids || [],
          });
          break;
        default: break;
      }
      if (result?.success !== false) {
        const wasEditing = !!editingId;
        const preserveKeys = {
          rooms: ['unit_id', 'bulk_mode'], beds: ['room_id'], residents: ['unit_id'],
          standing_alerts: [], shift_assignments: ['care_shift_id', 'unit_id'],
          patient_assignments: [],
        };
        const keep = preserveKeys[stepKey] || [];
        const preserved = {};
        keep.forEach(k => { if (formData[k] !== undefined) preserved[k] = formData[k]; });
        setFormData(preserved);
        setEditingId(null);
        setPatientData(null);

        // Build the toast label. Bulk room responses carry created/skipped counts.
        let toastName = savedLabel;
        if (stepKey === 'rooms' && result?.mode === 'bulk') {
          const parts = [`${result.created} room${result.created === 1 ? '' : 's'} created`];
          if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
          if (result.total_beds_created > 0) parts.push(`${result.total_beds_created} bed${result.total_beds_created === 1 ? '' : 's'}`);
          toastName = parts.join(' · ');
        }

        setJustSaved({ name: toastName, key: stepKey, edited: wasEditing });
        setTimeout(() => setJustSaved(null), 5000);
        fetchStatus();
        refreshStepData(stepKey);
        if (andContinue && status) {
          const next = status.steps.findIndex((s, i) => i > activeStep && !s.complete);
          if (next >= 0) setTimeout(() => setActiveStep(next), 400);
        }
      } else {
        setStepError({ stepKey, text: result?.error || 'Failed to save. Please check all required fields.' });
      }
    } catch (err) {
      const errText = err?.response?.data?.error || err?.message || 'An unexpected error occurred.';
      setStepError({ stepKey, text: errText });
      console.error('SetupWizard save error:', err);
    }
    finally { setSaving(false); }
  };

  const handleDeleteAssignment = async (stepKey, assignmentId) => {
    if (!window.confirm('Remove this assignment? Resident assignments for this CNA will also be removed.')) return;
    setStepError(null);
    try {
      const result = await adminApi.deleteShiftAssignment(assignmentId);
      if (result?.success !== false) {
        if (editingId === assignmentId) { setFormData({}); setEditingId(null); }
        setJustSaved({ name: 'Assignment', key: stepKey, deleted: true });
        setTimeout(() => setJustSaved(null), 4000);
        fetchStatus();
        refreshStepData(stepKey);
      } else {
        setStepError({ stepKey, text: result?.error || 'Failed to remove assignment.' });
      }
    } catch (err) {
      setStepError({ stepKey, text: err?.response?.data?.error || err?.message || 'Failed to remove assignment.' });
    }
  };

  // Generic soft-delete for rooms + units. Distinct from handleDeleteAssignment
  // (which has assignment-specific confirm copy and cascade semantics). Routes
  // to the right wrapper by stepKey. Backend returns a 409 when blocked
  // (active placements; unit delete cascades rooms+beds when empty) — that
  // message is surfaced in the same red "Save failed" banner as saves.
  const handleDeleteItem = async (stepKey, id, label) => {
    const confirmMsg = stepKey === 'units'
      ? `Deactivate ${label || 'this unit'}? Its rooms and beds will also be deactivated. This is blocked if any resident is currently placed in the unit.`
      : `Deactivate ${label || 'this room'}? This is blocked if any resident is currently placed in the room.`;
    if (!window.confirm(confirmMsg)) return;

    setStepError(null); setJustSaved(null);
    try {
      const result = stepKey === 'units'
        ? await adminApi.deleteUnit(id)
        : await adminApi.deleteRoom(id);

      if (result?.success !== false) {
        // If the deleted item was being edited, clear the form.
        if (editingId === id) { setFormData({}); setEditingId(null); }
        setJustSaved({ name: label || getStepNoun(stepKey), key: stepKey, deleted: true });
        setTimeout(() => setJustSaved(null), 4000);
        fetchStatus();
        refreshStepData(stepKey);
      } else {
        setStepError({ stepKey, text: result?.error || 'Failed to deactivate.' });
      }
    } catch (err) {
      // 409 guard messages (occupied room/unit) arrive here via the client.
      setStepError({ stepKey, text: err?.response?.data?.error || err?.message || 'Failed to deactivate.' });
    }
  };

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));
  const [editingId, setEditingId] = useState(null);

  const formHasData = () => {
    const skip = ['unit_id', 'room_id', 'care_shift_id', 'shift_assignment_id', 'severity', 'patient_ids', 'bulk_mode'];
    return Object.entries(formData).some(([k, v]) => {
      if (skip.includes(k)) return false;
      if (typeof v === 'boolean') return false;
      if (Array.isArray(v)) return v.length > 0;
      return v && String(v).trim() !== '';
    });
  };

  const patientAssignmentHasData = () => !!(formData.shift_assignment_id && patientData !== null);

  // Bulk-room form validation — used to enable/disable the save button.
  const bulkRoomRangeSize = () => {
    const start = parseInt(formData.start_number);
    const end   = parseInt(formData.end_number);
    if (!start || !end || start < 1 || end < start) return 0;
    return end - start + 1;
  };
  const bulkRoomValid = () => {
    if (!formData.unit_id) return false;
    const size = bulkRoomRangeSize();
    if (size <= 0 || size > ROOMS_BULK_MAX_RANGE) return false;
    const bedCount = parseInt(formData.bed_count) || 1;
    if (bedCount < 0 || bedCount > ROOMS_BULK_MAX_BED_COUNT) return false;
    return true;
  };

  const handleEdit = (stepKey, item) => {
    setStepError(null); setJustSaved(null);
    switch (stepKey) {
      case 'units':
        setFormData({ unit_id: item.unit_id, name: item.name, floor: item.floor || '' });
        setEditingId(item.unit_id);
        break;
      case 'rooms':
        // Edit a single existing room. bulk_mode:false forces the single-room
        // form; room_id in formData hides the Single/Bulk toggle (see renderStepForm).
        setFormData({
          room_id: item.room_id,
          unit_id: item.unit_id ? String(item.unit_id) : '',
          room_number: item.room_number || '',
          bed_count: item.bed_count != null ? String(item.bed_count) : '',
          bulk_mode: false,
        });
        setEditingId(item.room_id);
        break;
      case 'residents': {
        const r = item;
        setFormData({
          patient_id: r.patient_id, first_name: r.first_name, last_name: r.last_name,
          date_of_birth: r.date_of_birth, gender: r.gender,
          unit_id: r.unit_id ? String(r.unit_id) : '',
          room_id: r.room_id ? String(r.room_id) : '',
          bed_id:  r.bed_id  ? String(r.bed_id)  : '',
          allergies: r.allergies || '', medical_notes: r.medical_notes || '',
          emergency_contact_name: r.emergency_contact_name || '',
          emergency_contact_phone: r.emergency_contact_phone || '',
        });
        setEditingId(r.patient_id);
        break;
      }
      case 'standing_alerts': {
        const a = item;
        setFormData({
          standing_alert_id: a.standing_alert_id,
          patient_id: a.patient_id ? String(a.patient_id) : '',
          alert_type: a.alert_type || '', alert_label: a.alert_label || '',
          severity: a.severity || 'warning', details: a.details || '',
        });
        setEditingId(a.standing_alert_id);
        break;
      }
      case 'shift_assignments':
        setFormData({
          care_shift_id: String(item.care_shift_id || ''),
          membership_id: String(item.membership_id || ''),
          unit_id: String(item.unit_id || ''),
        });
        setEditingId(item.shift_assignment_id);
        break;
      default: break;
    }
    setTimeout(() => {
      const el = document.getElementById('setup-step-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  };

  const clearForm = () => { setFormData({}); setEditingId(null); setStepError(null); setPatientData(null); };

  const goToNextStep = () => {
    if (!status) return;
    const next = status.steps.findIndex((s, i) => i > activeStep);
    if (next >= 0) setActiveStep(next);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMuted }}>Loading setup status...</div>;
  if (!status) return null;
  const percent = status.percent;

  return (
    <div style={{ padding: 16 }}>
      {/* Progress card */}
      <div style={{ background: COLORS.surface, borderRadius: 12, padding: 20, marginBottom: 16, border: `1px solid ${COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: COLORS.text }}>Facility Setup Guide</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: COLORS.textMuted }}>{status.completed}/{status.total} steps complete</p>
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: percent === 100 ? COLORS.accent : COLORS.primary }}>{percent}%</div>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: COLORS.bg, overflow: 'hidden' }}>
          <div style={{ width: percent + '%', height: '100%', borderRadius: 4, background: percent === 100 ? COLORS.accent : COLORS.primary, transition: 'width 0.5s' }} />
        </div>
      </div>

      {status.steps.map((step, i) => {
        const isOpen = activeStep === i;
        const isComplete = step.complete;
        const isBlocked = i > 0 && !status.steps[i - 1].complete && i < 6;
        const isRepeatable = REPEATABLE_STEPS.includes(step.key);

        // Bulk rooms has its own validity check; all other steps use formHasData()
        const isBulkRooms = step.key === 'rooms' && formData.bulk_mode;
        const hasInputData = isBulkRooms ? bulkRoomValid() : formHasData();

        return (
          <div key={step.key} style={{
            background: COLORS.surface, borderRadius: 10,
            border: `1px solid ${isOpen ? COLORS.primary : COLORS.border}`,
            marginBottom: 8, overflow: 'hidden', opacity: isBlocked ? 0.5 : 1,
          }}>
            <div
              role="button"
              tabIndex={isBlocked ? -1 : 0}
              onClick={() => !isBlocked && setActiveStep(isOpen ? null : i)}
              onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !isBlocked) { e.preventDefault(); setActiveStep(isOpen ? null : i); }}}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: isBlocked ? 'not-allowed' : 'pointer' }}
            >
              <span style={{ fontSize: 22 }}>{isComplete ? '✅' : STEP_ICONS[i]}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: isComplete ? COLORS.accent : COLORS.text }}>
                  Step {i + 1}: {step.label}
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                  {step.description}
                  {step.count > 0 && <span style={{ color: COLORS.primary, fontWeight: 600 }}> &mdash; {step.count} created</span>}
                </div>
              </div>
              <span style={{ fontSize: 14, color: COLORS.textMuted, transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: '0.2s' }}>▼</span>
            </div>

            {isOpen && (
              <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${COLORS.border}` }}>
                {justSaved && justSaved.key === step.key && (
                  <div style={{
                    margin: '12px 0 0', padding: '10px 14px', borderRadius: 8,
                    background: COLORS.accentLight, border: `1px solid ${COLORS.accentAlpha}`,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span style={{ fontSize: 18 }}>✅</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.accent }}>
                        {justSaved.deleted
                          ? `${justSaved.name} ${step.key === 'shift_assignments' ? 'removed' : 'deactivated'}.`
                          : `${justSaved.name} ${justSaved.edited ? 'updated' : 'added'} successfully!`
                        }
                      </div>
                      {isRepeatable && !justSaved.edited && !justSaved.deleted && (
                        <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                          {step.key === 'patient_assignments'
                            ? 'Select another CNA to assign more residents, or click "Done — Go to Dashboard" when finished.'
                            : 'Add another below, or continue to the next step.'
                          }
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <CreatedItemsList
                  stepKey={step.key}
                  units={units} rooms={rooms} beds={beds}
                  residents={residents} alerts={alerts}
                  shifts={shifts} assignments={assignments}
                  onEdit={handleEdit}
                  onDelete={
                    step.key === 'shift_assignments' ? (id) => handleDeleteAssignment(step.key, id)
                    : (step.key === 'rooms' || step.key === 'units') ? (id, label) => handleDeleteItem(step.key, id, label)
                    : null
                  }
                  editingId={editingId}
                />

                <div id="setup-step-form" style={{ paddingTop: 12 }}>
                  {step.key === 'shift_assignments' && editingId && (
                    <div style={{
                      padding: '8px 12px', borderRadius: 8, marginBottom: 10,
                      background: COLORS.primaryLight, border: `1px solid ${COLORS.primaryAlpha}`,
                      fontSize: 12, color: COLORS.primary, fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <span>✎ Editing assignment &mdash; save to apply changes</span>
                      <button onClick={clearForm} style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>✕</button>
                    </div>
                  )}

                  {step.key === 'rooms' && editingId && (
                    <div style={{
                      padding: '8px 12px', borderRadius: 8, marginBottom: 10,
                      background: COLORS.primaryLight, border: `1px solid ${COLORS.primaryAlpha}`,
                      fontSize: 12, color: COLORS.primary, fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <span>✎ Editing room &mdash; save to apply changes</span>
                      <button onClick={clearForm} style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>✕</button>
                    </div>
                  )}

                  {step.key === 'units' && editingId && (
                    <div style={{
                      padding: '8px 12px', borderRadius: 8, marginBottom: 10,
                      background: COLORS.primaryLight, border: `1px solid ${COLORS.primaryAlpha}`,
                      fontSize: 12, color: COLORS.primary, fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <span>✎ Editing unit &mdash; save to apply changes</span>
                      <button onClick={clearForm} style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>✕</button>
                    </div>
                  )}

                  {renderStepForm(step.key, {
                    formData, update, units, rooms, beds, residents, shifts, assignments, staff, patientData, setPatientData, alerts,
                    editingId,
                    onShiftStatusChange: async (shiftId, newStatus) => {
                      try {
                        await adminApi.updateCareShift({ care_shift_id: shiftId, status: newStatus });
                        const today = new Date().toISOString().split('T')[0];
                        const date = formData.shift_date || today;
                        adminApi.getCareShifts(date).then(d => d.success && setShifts(d));
                        fetchStatus();
                      } catch (err) {
                        setStepError({ stepKey: step.key, text: err?.response?.data?.error || err.message });
                      }
                    },
                    onDateChange: (date) => {
                      adminApi.getCareShifts(date).then(d => d.success && setShifts(d)).catch(() => {});
                    },
                  })}

                  {stepError && stepError.stepKey === step.key && (
                    <div style={{
                      margin: '12px 0 0', padding: '12px 14px', borderRadius: 8,
                      background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`,
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                    }}>
                      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>❌</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.danger, marginBottom: 2 }}>Save failed</div>
                        <div style={{ fontSize: 12, color: COLORS.danger, lineHeight: 1.5, wordBreak: 'break-word' }}>{stepError.text}</div>
                      </div>
                      <button onClick={() => setStepError(null)} style={{ background: 'none', border: 'none', color: COLORS.danger, fontSize: 16, cursor: 'pointer', padding: '2px 6px', flexShrink: 0 }}>✕</button>
                    </div>
                  )}

                  {step.key !== 'shift_configs' && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                      {isRepeatable ? (
                        <>
                          {step.key === 'patient_assignments' ? (
                            patientAssignmentHasData() && (
                              <button onClick={() => handleSave(step.key, false)} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
                                {saving ? 'Saving...' : 'Save & Assign Another CNA'}
                              </button>
                            )
                          ) : (
                            hasInputData && (
                              <>
                                <button onClick={() => handleSave(step.key, false)} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
                                  {saving ? 'Saving...'
                                    : editingId ? 'Update ' + getStepNoun(step.key)
                                    : isBulkRooms ? `Create ${bulkRoomRangeSize()} Room${bulkRoomRangeSize() === 1 ? '' : 's'}`
                                    : 'Save & Add Another ' + getStepNoun(step.key)}
                                </button>
                                {!editingId && (
                                  <button onClick={() => handleSave(step.key, true)} disabled={saving} style={btnOutline}>
                                    {isBulkRooms ? 'Create & Next Step →' : 'Save & Next Step →'}
                                  </button>
                                )}
                                {editingId && (
                                  <button onClick={clearForm} style={btnOutline}>Cancel Edit</button>
                                )}
                              </>
                            )
                          )}
                          {step.key !== 'patient_assignments' && isComplete && !hasInputData && !editingId && (
                            <button onClick={goToNextStep} style={btnPrimary}>Done &mdash; Next Step &rarr;</button>
                          )}
                        </>
                      ) : (
                        <button onClick={() => handleSave(step.key, true)} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
                          {saving ? 'Saving...' : step.key === 'care_shifts' ? "Create Today's Shifts" : 'Save & Continue'}
                        </button>
                      )}
                      {isComplete && isRepeatable && step.key !== 'patient_assignments' && !hasInputData && !editingId && (
                        <span style={{ fontSize: 12, color: COLORS.textMuted, marginLeft: 4 }}>or fill in the form above to add more</span>
                      )}
                    </div>
                  )}

                  {step.key === 'shift_configs' && (
                    <button onClick={goToNextStep} style={{ ...btnPrimary, marginTop: 14 }}>Looks Good &mdash; Next Step &rarr;</button>
                  )}

                  {step.key === 'patient_assignments' && (
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` }}>
                      <button onClick={() => onComplete?.()} style={{ width: '100%', padding: '12px 0', borderRadius: 8, background: COLORS.accent, color: '#fff', border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                        ✓ Done Assigning Residents &mdash; Go to Dashboard
                      </button>
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: COLORS.textMuted, textAlign: 'center' }}>
                        You can always return to this step to reassign residents.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CreatedItemsList({ stepKey, units, rooms, beds, residents, alerts, shifts, assignments, onEdit, onDelete, editingId }) {
  const [expanded, setExpanded] = useState(false);
  const MAX_VISIBLE = 3;

  let items = [];
  const editable = ['units', 'rooms', 'residents', 'standing_alerts', 'shift_assignments'].includes(stepKey);
  // Rooms + units support soft-delete (deactivate) via the ✕ affordance;
  // shift_assignments keep their existing remove behavior.
  const deletable = ['rooms', 'units', 'shift_assignments'].includes(stepKey);

  switch (stepKey) {
    case 'units': items = units.map(u => ({ id: u.unit_id, label: decodeHtml(u.name), sub: u.floor ? 'Floor ' + u.floor : null, extra: (u.room_count || 0) + ' rooms', raw: u })); break;
    case 'rooms': items = rooms.map(r => ({ id: r.room_id, label: 'Rm ' + r.room_number, sub: decodeHtml(r.unit_name), extra: (r.actual_bed_count || 0) + ' beds', raw: r })); break;
    case 'beds': items = beds.map(b => ({ id: b.bed_id, label: 'Bed ' + b.bed_label, sub: 'Rm ' + b.room_number + ' \u2014 ' + decodeHtml(b.unit_name), extra: b.occupied_by ? decodeHtml(b.occupied_by) : 'Empty' })); break;
    case 'residents': items = residents.map(r => ({ id: r.patient_id, label: decodeHtml(r.first_name + ' ' + r.last_name), sub: 'Rm ' + (r.room_number || '?'), extra: r.alert_count > 0 ? r.alert_count + ' alerts' : null, raw: r })); break;
    case 'standing_alerts': items = alerts.map(a => ({ id: a.standing_alert_id, label: decodeHtml(a.alert_label), sub: decodeHtml(a.first_name + ' ' + a.last_name), extra: a.severity, raw: a })); break;
    case 'care_shifts': items = (shifts?.shifts || []).map(s => ({ id: s.care_shift_id, label: s.shift_name, sub: s.status, extra: s.assignment_count + ' CNA(s)' })); break;
    case 'shift_assignments': items = assignments.map(a => ({ id: a.shift_assignment_id, label: decodeHtml(a.staff_name), sub: a.shift_name + ' \u2014 ' + (a.unit_name || 'No unit'), extra: a.patient_count + ' residents', raw: a })); break;
    case 'patient_assignments': items = assignments.map(a => ({ id: a.shift_assignment_id, label: decodeHtml(a.staff_name), sub: a.shift_name + ' \u2014 ' + (a.unit_name || 'No unit'), extra: a.patient_count + ' resident(s) assigned', raw: a })); break;
    default: return null;
  }
  if (items.length === 0) return null;

  const hasMore = items.length > MAX_VISIBLE;
  const visibleItems = expanded ? items : items.slice(0, MAX_VISIBLE);

  return (
    <div style={{ marginTop: 12, marginBottom: 4 }}>
      <div
        role={hasMore ? 'button' : undefined}
        tabIndex={hasMore ? 0 : undefined}
        onClick={() => hasMore && setExpanded(!expanded)}
        onKeyDown={hasMore ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); }} : undefined}
        style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 6, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6, cursor: hasMore ? 'pointer' : 'default', userSelect: 'none' }}
      >
        <span>Already Created ({items.length})</span>
        {editable && <span style={{ fontWeight: 400, color: COLORS.textMuted, textTransform: 'none' }}>&mdash; tap to edit</span>}
        {hasMore && <span style={{ marginLeft: 'auto', fontSize: 11, color: COLORS.textMuted, fontWeight: 500, textTransform: 'none' }}>{expanded ? '▲ Collapse' : '▼ Show all ' + items.length}</span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: expanded ? 300 : undefined, overflowY: expanded ? 'auto' : undefined }}>
        {visibleItems.map(item => {
          const isEditing = editingId === item.id;
          return (
            <div key={item.id} style={{
              display: 'flex', alignItems: 'center',
              padding: '8px 12px', borderRadius: 6, fontSize: 13,
              background: isEditing ? COLORS.primaryLight : COLORS.bg,
              border: isEditing ? `1px solid ${COLORS.primary}` : '1px solid transparent',
              transition: 'background 0.15s',
            }}>
              <div
                role={editable ? 'button' : undefined}
                tabIndex={editable ? 0 : undefined}
                onClick={() => editable && onEdit && onEdit(stepKey, item.raw || item)}
                onKeyDown={editable ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit && onEdit(stepKey, item.raw || item); }} : undefined}
                style={{ flex: 1, cursor: editable ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <div>
                  <span style={{ fontWeight: 600, color: COLORS.text }}>{item.label}</span>
                  {item.sub && <span style={{ color: COLORS.textMuted }}> &mdash; {item.sub}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {item.extra && <span style={{ fontSize: 11, color: COLORS.textMuted }}>{item.extra}</span>}
                  {editable && <span style={{ fontSize: 11, color: COLORS.primary }}>✎</span>}
                </div>
              </div>
              {deletable && onDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(item.id, item.label); }}
                  title={stepKey === 'shift_assignments' ? 'Remove assignment' : 'Deactivate'}
                  style={{ marginLeft: 10, background: 'none', border: 'none', color: COLORS.danger, fontSize: 16, cursor: 'pointer', padding: '2px 6px', borderRadius: 4, lineHeight: 1, opacity: 0.7 }}
                >✕</button>
              )}
            </div>
          );
        })}
      </div>
      {hasMore && !expanded && (
        <button onClick={() => setExpanded(true)} style={{ marginTop: 4, background: 'none', border: 'none', color: COLORS.primary, fontSize: 12, cursor: 'pointer', padding: '4px 0', fontWeight: 600 }}>
          + {items.length - MAX_VISIBLE} more &mdash; show all
        </button>
      )}
    </div>
  );
}

// ── Mode toggle for rooms step ───────────────────────────────
function ModeToggle({ isBulk, onChange }) {
  const btnBase = {
    flex: 1, padding: '7px 10px', borderRadius: 6,
    border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s',
  };
  return (
    <div style={{ display: 'flex', gap: 2, background: COLORS.bg, borderRadius: 8, padding: 3 }}>
      <button
        onClick={() => onChange(false)}
        style={{
          ...btnBase,
          background: isBulk ? 'transparent' : COLORS.surface,
          color:      isBulk ? COLORS.textMuted : COLORS.text,
          boxShadow:  isBulk ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        Single Room
      </button>
      <button
        onClick={() => onChange(true)}
        style={{
          ...btnBase,
          background: isBulk ? COLORS.surface : 'transparent',
          color:      isBulk ? COLORS.text : COLORS.textMuted,
          boxShadow:  isBulk ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
        }}
      >
        Bulk Create
      </button>
    </div>
  );
}

function renderStepForm(key, ctx) {
  const { formData, update, units, rooms, beds, residents, shifts, assignments, staff, alerts } = ctx;
  switch (key) {
    case 'units': return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Field label="Unit Name *" placeholder='e.g., &quot;1st Floor&quot;' value={formData.name || ''} onChange={v => update('name', v)} />
      <Field label="Floor" placeholder="e.g., 1" value={formData.floor || ''} onChange={v => update('floor', v)} />
      {!!formData.unit_id && (
        <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
          Renaming is blocked while residents are placed in this unit; the floor can always be changed.
        </div>
      )}
    </div>);
    case 'rooms': {
      const isEditing = !!formData.room_id;
      const isBulk = !isEditing && !!formData.bulk_mode;
      const start  = parseInt(formData.start_number);
      const end    = parseInt(formData.end_number);
      const bedCount = parseInt(formData.bed_count) || 0;
      const rangeSize = (isBulk && start && end && end >= start) ? (end - start + 1) : 0;
      const totalBeds = rangeSize * bedCount;
      const rangeTooLarge = rangeSize > ROOMS_BULK_MAX_RANGE;
      const bedsTooMany   = bedCount > ROOMS_BULK_MAX_BED_COUNT;

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Single/Bulk toggle only when creating — hidden while editing one room. */}
          {!isEditing && (
            <ModeToggle
              isBulk={isBulk}
              onChange={v => {
                update('bulk_mode', v);
                // Clear mode-specific fields when switching so stale values don't submit
                if (v) {
                  update('room_number', '');
                } else {
                  update('start_number', '');
                  update('end_number', '');
                }
              }}
            />
          )}

          {/* Unit is fixed while editing (room can't move units here) — show read-only. */}
          {isEditing ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Unit</div>
              <div style={{
                padding: '10px 12px', borderRadius: 8, fontSize: 14,
                background: COLORS.bg, color: COLORS.textMuted,
                border: `1px solid ${COLORS.border}`,
              }}>
                {decodeHtml(units.find(u => String(u.unit_id) === String(formData.unit_id))?.name || 'Current unit')}
              </div>
            </div>
          ) : (
            <Select
              label="Unit *"
              value={formData.unit_id || ''}
              onChange={v => update('unit_id', v)}
              options={units.map(u => ({ value: u.unit_id, label: decodeHtml(u.name) }))}
              placeholder="Select unit"
            />
          )}

          {isBulk ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field
                  label="Starting Room # *"
                  placeholder="e.g., 101"
                  type="number"
                  value={formData.start_number || ''}
                  onChange={v => update('start_number', v)}
                />
                <Field
                  label="Ending Room # *"
                  placeholder="e.g., 120"
                  type="number"
                  value={formData.end_number || ''}
                  onChange={v => update('end_number', v)}
                />
              </div>
              <Field
                label={`Beds Per Room (1\u2013${ROOMS_BULK_MAX_BED_COUNT})`}
                placeholder="1"
                type="number"
                value={formData.bed_count || ''}
                onChange={v => update('bed_count', v)}
              />

              {rangeSize > 0 && !rangeTooLarge && !bedsTooMany && (
                <div style={{
                  padding: '10px 12px', background: COLORS.primaryLight,
                  borderRadius: 8, fontSize: 12, color: COLORS.primary,
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>📋</span>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>
                      Will create {rangeSize} room{rangeSize === 1 ? '' : 's'} ({start}&ndash;{end})
                      {totalBeds > 0 && `, ${totalBeds} bed${totalBeds === 1 ? '' : 's'} total`}
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 400 }}>
                      Rooms with numbers already in use will be skipped automatically.
                    </div>
                  </div>
                </div>
              )}

              {rangeTooLarge && (
                <div style={{
                  padding: '10px 12px', background: COLORS.dangerLight,
                  border: `1px solid ${COLORS.danger}`, borderRadius: 8,
                  fontSize: 12, color: COLORS.danger, fontWeight: 600,
                }}>
                  ⚠️ Range too large ({rangeSize} rooms). Maximum {ROOMS_BULK_MAX_RANGE} rooms per bulk create.
                </div>
              )}

              {bedsTooMany && (
                <div style={{
                  padding: '10px 12px', background: COLORS.dangerLight,
                  border: `1px solid ${COLORS.danger}`, borderRadius: 8,
                  fontSize: 12, color: COLORS.danger, fontWeight: 600,
                }}>
                  ⚠️ Maximum {ROOMS_BULK_MAX_BED_COUNT} beds per room.
                </div>
              )}
            </>
          ) : (
            <>
              <Field
                label="Room Number *"
                placeholder='e.g., &quot;101&quot;'
                value={formData.room_number || ''}
                onChange={v => update('room_number', v)}
              />
              <Field
                label="Bed Count"
                placeholder="1"
                type="number"
                value={formData.bed_count || ''}
                onChange={v => update('bed_count', v)}
              />
              {isEditing && (
                <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
                  Renaming is blocked while residents are placed in this room; bed count can only go as low as the number of occupied beds.
                </div>
              )}
            </>
          )}
        </div>
      );
    }
    case 'beds': return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Select label="Room *" value={formData.room_id || ''} onChange={v => update('room_id', v)} options={rooms.map(r => ({ value: r.room_id, label: decodeHtml(r.unit_name) + ' \u2014 Rm ' + r.room_number }))} placeholder="Select room" />
      <Field label="Bed Label *" placeholder='e.g., &quot;A&quot; or &quot;Window&quot;' value={formData.bed_label || ''} onChange={v => update('bed_label', v)} />
    </div>);
    case 'residents': return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SectionLabel text="Personal Information" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="First Name *" value={formData.first_name || ''} onChange={v => update('first_name', v)} />
        <Field label="Last Name *" value={formData.last_name || ''} onChange={v => update('last_name', v)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Date of Birth *" type="date" value={formData.date_of_birth || ''} onChange={v => update('date_of_birth', v)} />
        <Select label="Gender *" value={formData.gender || ''} onChange={v => update('gender', v)} options={[{value:'Male',label:'Male'},{value:'Female',label:'Female'},{value:'Other',label:'Other'}]} placeholder="Select" />
      </div>
      <SectionLabel text="Location Assignment" />
      <Select label="Unit *" value={formData.unit_id || ''} onChange={v => { update('unit_id', v); update('room_id', ''); update('bed_id', ''); }} options={units.map(u => ({ value: u.unit_id, label: decodeHtml(u.name) }))} placeholder="Select unit" />
      <Select label="Room *" value={formData.room_id || ''} onChange={v => { update('room_id', v); update('bed_id', ''); }} options={rooms.filter(r => !formData.unit_id || String(r.unit_id) === String(formData.unit_id)).map(r => ({ value: r.room_id, label: 'Rm ' + r.room_number }))} placeholder="Select room" />
      <Select label="Bed *" value={formData.bed_id || ''} onChange={v => update('bed_id', v)} options={beds.filter(b => !formData.room_id || String(b.room_id) === String(formData.room_id)).map(b => ({ value: b.bed_id, label: 'Bed ' + b.bed_label + (b.occupied_by ? ' (Occupied: ' + decodeHtml(b.occupied_by) + ')' : '') }))} placeholder="Select bed" />
      <SectionLabel text="Medical (Optional)" />
      <AllergyPicker value={formData.allergies || ''} onChange={v => update('allergies', v)} />
      <Field label="Medical Notes" value={formData.medical_notes || ''} onChange={v => update('medical_notes', v)} multiline placeholder="General care notes" />
      <SectionLabel text="Emergency Contact (Optional)" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Contact Name" value={formData.emergency_contact_name || ''} onChange={v => update('emergency_contact_name', v)} />
        <Field label="Contact Phone" value={formData.emergency_contact_phone || ''} onChange={v => update('emergency_contact_phone', v)} />
      </div>
    </div>);
    case 'standing_alerts': return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Select label="Resident *" value={formData.patient_id || ''} onChange={v => update('patient_id', v)} options={residents.map(r => ({ value: r.patient_id, label: decodeHtml(r.first_name + ' ' + r.last_name) + ' \u2014 Rm ' + (r.room_number || '?') }))} placeholder="Select resident" />
      <Select label="Alert Type *" value={formData.alert_type || ''} onChange={v => {
        update('alert_type', v);
        const labels = { fall_risk:'Fall Risk', dnr:'DNR', diet_restriction:'Diet: ', diabetic:'Diabetic', isolation:'Isolation', elopement_risk:'Elopement Risk', aspiration_risk:'Aspiration Risk', allergy:'Allergy: ', custom:'' };
        update('alert_label', labels[v] || '');
        const sev = { fall_risk:'critical', dnr:'critical', elopement_risk:'critical', aspiration_risk:'warning', diet_restriction:'warning', diabetic:'info', isolation:'warning', allergy:'warning', custom:'info' };
        update('severity', sev[v] || 'warning');
      }} options={[{value:'fall_risk',label:'Fall Risk'},{value:'dnr',label:'DNR'},{value:'diet_restriction',label:'Diet Restriction'},{value:'diabetic',label:'Diabetic'},{value:'isolation',label:'Isolation'},{value:'elopement_risk',label:'Elopement Risk'},{value:'aspiration_risk',label:'Aspiration Risk'},{value:'allergy',label:'Allergy'},{value:'custom',label:'Custom'}]} placeholder="Select type" />
      <Field label="Alert Label *" placeholder='e.g., &quot;Diet: Pureed&quot;' value={formData.alert_label || ''} onChange={v => update('alert_label', v)} />
      <Select label="Severity" value={formData.severity || 'warning'} onChange={v => update('severity', v)} options={[{value:'info',label:'Info (Blue)'},{value:'warning',label:'Warning (Orange)'},{value:'critical',label:'Critical (Red)'}]} />
      <Field label="Details" value={formData.details || ''} onChange={v => update('details', v)} placeholder="Additional context" />
    </div>);
    case 'shift_configs': return (<div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6 }}>
      <p style={{ margin: '0 0 8px' }}>Three default shifts were created during registration:</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {[['Day Shift','7:00 AM \u2192 3:00 PM'],['Evening Shift','3:00 PM \u2192 11:00 PM'],['Night Shift','11:00 PM \u2192 7:00 AM']].map(([n,t]) => (
          <div key={n} style={{ padding: '8px 12px', background: COLORS.bg, borderRadius: 6, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600, color: COLORS.text }}>{n}</span><span style={{ color: COLORS.textMuted }}>{t}</span>
          </div>
        ))}
      </div>
      <p style={{ margin: '12px 0 0', fontStyle: 'italic' }}>If these times are correct, continue to the next step.</p>
    </div>);
    case 'care_shifts': {
      const d = new Date();
      const today = d.toLocaleDateString('en-CA');
      const tomorrow = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).toLocaleDateString('en-CA');
      const selectedDate = formData.shift_date || today;
      const statusColors = { scheduled: COLORS.textLight, active: COLORS.accent, completed: COLORS.textMuted, cancelled: COLORS.danger };
      // Parallel background dict — use alpha tokens so pills theme correctly.
      // Previous `statusColors[s.status] + '22'` concat produced invalid CSS
      // ("var(--color-accent)22") since statusColors values are CSS variables.
      // scheduled and completed both use borderAlpha as a neutral tint since
      // textLight/textMuted don't have dedicated alpha variants.
      const statusBgColors = { scheduled: COLORS.borderAlpha, active: COLORS.accentAlpha, completed: COLORS.borderAlpha, cancelled: COLORS.dangerAlpha };
      return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SectionLabel text="Select Date" />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => { update('shift_date', today); ctx.onDateChange && ctx.onDateChange(today); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${selectedDate === today ? COLORS.primary : COLORS.border}`, background: selectedDate === today ? COLORS.primaryLight : COLORS.surface, color: selectedDate === today ? COLORS.primary : COLORS.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Today
          </button>
          <button onClick={() => { update('shift_date', tomorrow); ctx.onDateChange && ctx.onDateChange(tomorrow); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${selectedDate === tomorrow ? COLORS.primary : COLORS.border}`, background: selectedDate === tomorrow ? COLORS.primaryLight : COLORS.surface, color: selectedDate === tomorrow ? COLORS.primary : COLORS.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Tomorrow
          </button>
          <Field label="" type="date" value={selectedDate} onChange={v => { update('shift_date', v); ctx.onDateChange && ctx.onDateChange(v); }} />
        </div>
        <p style={{ margin: '4px 0', fontSize: 13, color: COLORS.textMuted }}>
          Creates all shift configurations for <strong style={{ color: COLORS.text }}>{new Date(selectedDate + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</strong>.
        </p>
        {shifts?.shifts?.length > 0 && (<div style={{ marginTop: 4 }}>
          <SectionLabel text={'Shifts for ' + (selectedDate === today ? 'Today' : selectedDate === tomorrow ? 'Tomorrow' : selectedDate)} />
          {shifts.shifts.map(s => (
            <div key={s.care_shift_id} style={{ padding: '10px 12px', background: COLORS.bg, borderRadius: 8, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontWeight: 600, color: COLORS.text }}>{s.shift_name}</span>
                <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 10, background: statusBgColors[s.status] || COLORS.borderAlpha, color: statusColors[s.status], fontWeight: 600 }}>{s.status}</span>
                <span style={{ marginLeft: 8, fontSize: 11, color: COLORS.textMuted }}>{s.assignment_count} CNA(s)</span>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {s.status === 'scheduled' && (
                  <button onClick={() => ctx.onShiftStatusChange && ctx.onShiftStatusChange(s.care_shift_id, 'active')}
                    style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${COLORS.accent}`, background: COLORS.surface, color: COLORS.accent, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Activate
                  </button>
                )}
                {s.status === 'active' && (<>
                  <button onClick={() => window.confirm('Reset this shift to scheduled?') && ctx.onShiftStatusChange && ctx.onShiftStatusChange(s.care_shift_id, 'scheduled')}
                    style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textMuted, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Reset
                  </button>
                  <button onClick={() => ctx.onShiftStatusChange && ctx.onShiftStatusChange(s.care_shift_id, 'completed')}
                    style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textMuted, fontSize: 11, fontWeight: 600, cursor: 'pointer', marginLeft: 4 }}>
                    End Shift
                  </button>
                </>)}
                {s.status === 'completed' && (
                  <button onClick={() => window.confirm('Reopen this shift?') && ctx.onShiftStatusChange && ctx.onShiftStatusChange(s.care_shift_id, 'active')}
                    style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${COLORS.warning}`, background: COLORS.surface, color: COLORS.warning, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Reopen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>)}
        {shifts?.shifts?.length === 0 && (
          <p style={{ margin: 0, fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic' }}>No shifts created for this date yet.</p>
        )}
      </div>);
    }
    case 'shift_assignments': return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Select label="Care Shift *" value={formData.care_shift_id || ''} onChange={v => update('care_shift_id', v)} options={(shifts?.shifts || []).map(s => ({ value: s.care_shift_id, label: s.shift_name + ' \u2014 ' + s.status }))} placeholder="Select shift" />
      <Select label="Staff Member *" value={formData.membership_id || ''} onChange={v => update('membership_id', v)} options={staff.map(s => ({ value: s.membership_id, label: decodeHtml(s.first_name + ' ' + s.last_name) + ' (' + s.role + ')' }))} placeholder="Select CNA" />
      <Select label="Unit *" value={formData.unit_id || ''} onChange={v => update('unit_id', v)} options={units.map(u => ({ value: u.unit_id, label: decodeHtml(u.name) }))} placeholder="Select unit" />
    </div>);
    case 'patient_assignments': {
      const sel = assignments.find(a => String(a.shift_assignment_id) === String(formData.shift_assignment_id));
      const assignedResidents = (ctx.patientData?.assigned || []).map(p => ({ patient_id: p.id ?? p.patient_id, first_name: p.first_name, last_name: p.last_name, room_number: p.room_number, wasAssigned: true }));
      const availableResidents = (ctx.patientData?.available || []).map(p => ({ patient_id: p.patient_id, first_name: p.first_name, last_name: p.last_name, room_number: p.room_number, wasAssigned: false }));
      const allResidents = [...assignedResidents, ...availableResidents];
      const selectedIds = formData.patient_ids || [];

      function toggleResident(patientId) {
        const ids = formData.patient_ids || [];
        update('patient_ids', ids.includes(patientId) ? ids.filter(id => id !== patientId) : [...ids, patientId]);
      }

      return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Select
          label="Shift Assignment *"
          value={formData.shift_assignment_id || ''}
          onChange={v => {
            update('shift_assignment_id', v);
            update('patient_ids', []);
            ctx.setPatientData(null);
            if (v) {
              adminApi.getPatientAssignments(v).then(d => {
                if (d.success) {
                  ctx.setPatientData(d);
                  const preSelected = (d.assigned || []).map(p => p.id ?? p.patient_id);
                  update('patient_ids', preSelected);
                }
              });
            }
          }}
          options={assignments.map(a => ({ value: a.shift_assignment_id, label: decodeHtml(a.staff_name) + ' \u2014 ' + a.shift_name + ' \u2014 ' + (a.unit_name || '') }))}
          placeholder="Select assignment"
        />
        {sel && ctx.patientData && (
          <div>
            {allResidents.length === 0 ? (
              <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic' }}>No residents in this unit.</div>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  All residents &mdash; tap to assign or unassign
                </div>
                {allResidents.map(p => {
                  const checked = selectedIds.includes(p.patient_id);
                  const wasAssigned = p.wasAssigned;
                  const changed = checked !== wasAssigned;
                  return (
                    <div key={p.patient_id} role="button" tabIndex={0}
                      onClick={() => toggleResident(p.patient_id)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleResident(p.patient_id); }}}
                      style={{ padding: '10px 12px', marginBottom: 6, borderRadius: 8, border: `1px solid ${checked ? COLORS.primary : COLORS.border}`, background: checked ? COLORS.primaryLight : COLORS.bg, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                    >
                      <div style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, background: checked ? COLORS.primary : COLORS.surface, border: `2px solid ${checked ? COLORS.primary : COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#fff', fontWeight: 700 }}>
                        {checked ? '✓' : ''}
                      </div>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, color: COLORS.text, fontSize: 13 }}>{decodeHtml(p.first_name)} {decodeHtml(p.last_name)}</span>
                        <span style={{ color: COLORS.textMuted, fontSize: 12, marginLeft: 8 }}>Rm {p.room_number}</span>
                      </div>
                      {changed && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: checked ? COLORS.primaryLight : COLORS.dangerLight, color: checked ? COLORS.primary : COLORS.danger }}>
                          {checked ? '+ Adding' : '\u2212 Removing'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>);
    }
    // eslint-disable-next-line no-unused-vars
    default: void alerts; return <div style={{ color: COLORS.textMuted, fontSize: 13 }}>This step is managed automatically.</div>;
  }
}

function SectionLabel({ text }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', marginTop: 8, paddingBottom: 2, borderBottom: `1px solid ${COLORS.border}` }}>
      {text}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', multiline }) {
  const id = useId();
  const s = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: `1px solid ${COLORS.border}`, fontSize: 14,
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    background: COLORS.surface, color: COLORS.text,
  };
  return (
    <div>
      {label && <label htmlFor={id} style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>}
      {multiline
        ? <textarea id={id} style={{ ...s, minHeight: 60, resize: 'vertical' }} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
        : <input id={id} style={s} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      }
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder }) {
  const id = useId();
  return (
    <div>
      {label && <label htmlFor={id} style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>}
      <select
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 8,
          border: `1px solid ${COLORS.border}`, fontSize: 14,
          background: COLORS.surface, color: COLORS.text,
          cursor: 'pointer', boxSizing: 'border-box',
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

const btnPrimary = {
  padding: '10px 20px', borderRadius: 8, border: 'none',
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
  background: COLORS.primary, color: '#fff',
};
const btnOutline = {
  padding: '10px 20px', borderRadius: 8,
  border: `1px solid ${COLORS.primary}`,
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
  background: 'transparent', color: COLORS.primary,
};